import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import Loader from "../components/Loader";
import authService from "../services/authService";
import uploadService from "../services/uploadService";

const Settings = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(authService.getCurrentUser());
  const [loading, setLoading] = useState(true);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_url || "");

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const profile = await authService.getProfile();
        if (profile?.user) {
          setUser(profile.user);
          if (profile.user.avatar_url) {
            setAvatarPreview(profile.user.avatar_url);
          }
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Unable to load profile data.");
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, []);

  const uploadAvatar = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      return toast.error("Avatar image must be 5 MB or smaller.");
    }

    try {
      setAvatarLoading(true);
      const response = await uploadService.uploadAvatar(file);
      const imageUrl = response?.avatar_url;
      if (imageUrl) {
        const updatedUser = { ...(user || {}), avatar_url: imageUrl };
        authService.setCurrentUser(updatedUser);
        setUser(updatedUser);
        setAvatarPreview(imageUrl);
      }
      toast.success(response?.message || "Profile avatar updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to upload avatar image.");
    } finally {
      setAvatarLoading(false);
      event.target.value = "";
    }
  };

  const logout = () => {
    authService.logout();
    navigate("/login");
  };

  const initials = (user?.full_name || user?.username || "U")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return (
    <div className="d-flex flex-column min-vh-100 app-shell">
      <Navbar toggleSidebar={() => setSidebarOpen((prev) => !prev)} />

      <div className="d-flex flex-grow-1 position-relative">
        <Sidebar isOpen={sidebarOpen} closeMobileSidebar={() => setSidebarOpen(false)} />

        <main className="app-main flex-grow-1 overflow-auto">
          <div className="container-fluid admin-page-wrap">
            {/* Header */}
            <div className="admin-page-header">
              <div>
                <span className="admin-eyebrow">ACCOUNT & PROFILE</span>
                <h2>User Profile Settings</h2>
                <p>Manage your account identity, profile photo, and security details.</p>
              </div>
            </div>

            {loading ? (
              <Loader message="Loading profile settings..." />
            ) : (
              <div className="row justify-content-center">
                <div className="col-12 col-lg-8 col-xl-7">
                  {/* Premium Profile Card */}
                  <div className="admin-panel shadow-lg border-gold rounded-4 overflow-hidden">
                    {/* Header Banner */}
                    <div
                      className="p-4 p-md-5 text-center position-relative"
                      style={{
                        background: "linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(18, 24, 39, 0.95) 100%)",
                        borderBottom: "1px solid rgba(212, 175, 55, 0.2)"
                      }}
                    >
                      {/* Avatar Upload */}
                      <div className="position-relative d-inline-block mb-3">
                        <div
                          className="rounded-circle overflow-hidden shadow-lg border border-3 border-gold"
                          style={{ width: "110px", height: "110px", margin: "0 auto", background: "#0D111C" }}
                        >
                          {avatarPreview ? (
                            <img
                              src={avatarPreview}
                              alt="User avatar"
                              className="w-100 h-100 object-fit-cover"
                            />
                          ) : (
                            <div className="w-100 h-100 d-grid place-items-center text-gold fs-2 fw-bold bg-dark">
                              {initials}
                            </div>
                          )}
                        </div>

                        <label
                          className={`position-absolute bottom-0 end-0 bg-warning text-dark rounded-circle p-2 shadow border border-2 border-dark cursor-pointer transition-transform ${
                            avatarLoading ? "opacity-50 pointer-events-none" : ""
                          }`}
                          style={{ width: "36px", height: "36px", transform: "translate(4px, 4px)" }}
                          title="Upload new profile picture"
                        >
                          {avatarLoading ? (
                            <span className="spinner-border spinner-border-sm d-block mx-auto" />
                          ) : (
                            <i className="bi bi-camera-fill d-grid place-items-center fs-6" />
                          )}
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            onChange={uploadAvatar}
                            disabled={avatarLoading}
                            className="d-none"
                          />
                        </label>
                      </div>

                      <h3 className="fw-bold text-light mb-1">
                        {user?.full_name || user?.username || "User Profile"}
                      </h3>
                      <p className="text-gold mb-2 font-monospace">@{user?.username || "user"}</p>

                      <span className="badge bg-warning text-dark px-3 py-2 fw-bold text-uppercase rounded-pill shadow-sm">
                        <i className="bi bi-person-badge-fill me-1" />
                        {String(user?.role || "").replaceAll("_", " ")}
                      </span>
                    </div>

                    {/* Stats Highlights Grid */}
                    <div className="row g-0 border-bottom border-secondary border-opacity-25 bg-dark bg-opacity-50">
                      <div className="col-6 col-md-3 p-3 text-center border-end border-secondary border-opacity-25">
                        <span className="text-muted small text-uppercase fw-bold d-block mb-1">User ID</span>
                        <strong className="text-light fs-5">#{user?.id || "-"}</strong>
                      </div>

                      <div className="col-6 col-md-3 p-3 text-center border-end border-secondary border-opacity-25">
                        <span className="text-muted small text-uppercase fw-bold d-block mb-1">Available Coins</span>
                        <strong className="text-success fs-5">🪙 {Number(user?.wallet || 0).toLocaleString("en-IN")}</strong>
                      </div>

                      <div className="col-6 col-md-3 p-3 text-center border-end border-secondary border-opacity-25">
                        <span className="text-muted small text-uppercase fw-bold d-block mb-1">Status</span>
                        <span className="badge bg-success px-2 py-1 fs-6">ACTIVE</span>
                      </div>

                      <div className="col-6 col-md-3 p-3 text-center">
                        <span className="text-muted small text-uppercase fw-bold d-block mb-1">Session</span>
                        <span className="badge bg-info text-dark px-2 py-1 fs-6">VERIFIED</span>
                      </div>
                    </div>

                    {/* Detailed Account Information */}
                    <div className="p-4 p-md-5">
                      <h5 className="fw-bold text-light mb-3 pb-2 border-bottom border-secondary border-opacity-25">
                        <i className="bi bi-person-lines-fill text-gold me-2" />
                        Account Identity Details
                      </h5>

                      <div className="row g-3 mb-4">
                        <div className="col-12 col-md-6">
                          <div className="p-3 rounded-3 bg-dark border border-secondary border-opacity-25">
                            <span className="text-muted small d-block">Full Name</span>
                            <strong className="text-light fs-6">{user?.full_name || "Not set"}</strong>
                          </div>
                        </div>

                        <div className="col-12 col-md-6">
                          <div className="p-3 rounded-3 bg-dark border border-secondary border-opacity-25">
                            <span className="text-muted small d-block">Username</span>
                            <strong className="text-light fs-6">@{user?.username}</strong>
                          </div>
                        </div>

                        <div className="col-12 col-md-6">
                          <div className="p-3 rounded-3 bg-dark border border-secondary border-opacity-25">
                            <span className="text-muted small d-block">Account Role</span>
                            <strong className="text-gold text-uppercase fs-6">
                              {String(user?.role || "").replaceAll("_", " ")}
                            </strong>
                          </div>
                        </div>

                        <div className="col-12 col-md-6">
                          <div className="p-3 rounded-3 bg-dark border border-secondary border-opacity-25">
                            <span className="text-muted small d-block">Current Wallet Balance</span>
                            <strong className="text-success fs-6">
                              🪙 {Number(user?.wallet || 0).toLocaleString("en-IN")} Coins
                            </strong>
                          </div>
                        </div>
                      </div>

                      {/* Security Box */}
                      <div className="d-flex align-items-start gap-3 p-3 rounded-3 bg-warning bg-opacity-10 border border-warning border-opacity-25 mb-4">
                        <i className="bi bi-shield-check text-warning fs-3 flex-shrink-0 mt-1" />
                        <div>
                          <h6 className="fw-bold text-light mb-1">Protected Account Session</h6>
                          <p className="text-muted small mb-0">
                            Your account is authenticated with secure JWT token protection. Contact your administrator if you need to reset passwords or transfer permissions.
                          </p>
                        </div>
                      </div>

                      {/* Logout Action */}
                      <button
                        type="button"
                        className="btn btn-outline-danger w-100 py-3 rounded-3 fw-bold d-flex align-items-center justify-content-center gap-2"
                        onClick={logout}
                      >
                        <i className="bi bi-box-arrow-right fs-5" />
                        Sign Out of Account
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Settings;
